<template>
  <div ref="appHeader">
    <UHeader :class="{ 'hideheader': hideHeader }" class="uheader w-full pointer-events-auto"
      :toggle="{ class: 'hidden' }">
      <template #left>
        <NuxtLink to="/" aria-label="Go to homepage">
        <Logo :isDark="colorMode.value !== 'dark'" class="w-22" />
      </NuxtLink>
        
      </template>
      
      <template #right>
        <div class="flex items-center gap-2">
          <WorkbookcurrencySelector v-if="auth.isAuthenticated" />
          <!-- BEGIN USER DROPDOWN -->
          <UDropdownMenu v-if="auth.isAuthenticated" :items="userDropdownItems" class="user-dropdown cursor-pointer">
            <UButton size="sm" color="neutral" variant="ghost" trailing-icon="i-lucide-chevron-down"
              aria-label="User menu" :label="auth.isAdmin ? `Admin: ${auth.user.email}` : auth.user?.email"
              class="min-w-[56px] font-medium" tabindex="-1" @mousedown.prevent />
          </UDropdownMenu>
          <!-- END USER DROPDOWN -->
          <UButton v-else size="lg" color="neutral" variant="link" icon="workbook:user"  aria-label="Login"
            @click="goLogin"/>            
          

          <UButton class="cursor-pointer" icon="workbook:search" color="neutral" variant="link" size="lg" aria-label="Search"
            @click="searchOpen = true" />

          
          

            <UButton v-if="cart.totalModels > 0" to="/cart" class="cursor-pointer relative p-0.5!" size="xl" icon="workbook:cart" color="neutral" variant="link" aria-label="Open cart">
            <span v-if="cartReady && cart.totalModels > 0"
              class="absolute top-0 right-0 bg-black text-white text-[10px] leading-none px-1.5 py-1 rounded-none">
              {{ cart.totalModels }}
            </span>
            </UButton>
          
          
          
            <UButton to="/products" size="lg" class="cursor-pointer" color="neutral" variant="link" icon="workbook:line-overview" aria-label="Logout" />
          
          <UiBurger class="pt-1 z-50" :active="menuOpen" @click="toggleMenu" />
        </div>
      </template>
    </UHeader>
    <!-- MEGA MENU-->
    <UiMegaMenu :menuOpen="menuOpen" @close="closeMenu" />
    <!-- END MEGA-->
    <!-- SEARCH MODAL -->
    <SearchModal :open="searchOpen" @update:open="searchOpen = $event" />
  </div>
</template>
<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'

import { useCartStore } from '@/store/cart'
import { useAuthStore } from '@/store/auth'
import { useSettingsStore } from '@/store/settings'
import { onClickOutside, useEventListener } from '@vueuse/core'
import ColorModeButton from './ColorModeButton.vue'
import WorkbookcurrencySelector from './workbook/CurrencySelector.vue'
import SearchModal from './SearchModal.vue'
import { useWindowScroll } from '@vueuse/core'



const route = useRoute()
const cart = useCartStore()
const auth = useAuthStore()
const settings = useSettingsStore()
const colorMode = useColorMode()

const { $logoutFirebase } = useNuxtApp()

const isDev = import.meta.dev

const menuOpen = ref(false)
const appHeader = ref<HTMLElement | null>(null)

const { y } = useWindowScroll() // y = window.scrollY reaktivt
const lastY = ref(0)
const hideHeader = ref(true)

const searchOpen = ref(false)

const toggleMenu = () => {
  menuOpen.value = !menuOpen.value
}

const closeMenu = () => {
  menuOpen.value = false
}

watch(y, (newY) => {
  if (newY > lastY.value && newY > 120) {
    hideHeader.value = true // scrollar ned
  } else if (newY < lastY.value) {
    hideHeader.value = false // scrollar upp
  }
  lastY.value = newY
})

onClickOutside(appHeader, closeMenu)

const openSearchModal = () => {
  searchOpen.value = true;
}

onMounted(() => {
  hideHeader.value = false;
  cart.hydrate()
  settings.hydrate()
  useEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeMenu()
    }
  })

  if (typeof globalThis !== 'undefined' && 'window' in globalThis) {
    ;(globalThis as any).openSearchModal = openSearchModal
  }
})
const cartReady = computed(() => cart.hydrated)



async function logout() {
  await $logoutFirebase()
  auth.clearUser()
  navigateTo('/')
}
function goLogin() {
  navigateTo('/login?redirect=' + encodeURIComponent(route.fullPath))
}

const userDropdownItems = computed(() => {
  const items = [];
  console.log('[AppHeader] Building menu - isAdmin:', auth.isAdmin, 'isRep:', auth.isRep, 'isRepAdmin:', auth.isRepAdmin)

  if (auth.isAdmin || auth.isRep || auth.isRepAdmin) {
    items.push({
      label: 'Manage Orders',
      onSelect: () => navigateTo('/admin-orders')
    })

    if (isDev) {
      items.push({
        label: 'Manage Dealers',
        onSelect: () => navigateTo('/admin-dealers')
      })

      items.push({
        label: 'Manage Users',
        onSelect: () => navigateTo('/admin-users')
      })
    }
  }

  if ((auth.isAdmin || auth.isRepAdmin) && isDev) {
    items.push({
      label: 'Manage Reps',
      onSelect: () => navigateTo('/admin-reps')
    })
  }

  items.push({
    label: 'My Orders',
    onSelect: () => navigateTo('/orders')
  })

  items.push({
    label: 'Saved Carts',
    onSelect: () => navigateTo('/saved-carts')
  })

  items.push({
    label: 'Log Out',
    onSelect: logout
  })
  return items
})

const { data: mainnav } = await useAsyncData('mainnav', () =>
  queryCollection('mainnav').first()
)

const mainMenuItems = computed(() => {
  const menu = Array.isArray(mainnav.value?.menuitems) ? mainnav.value.menuitems : []
  return menu.map((item: any) => ({
    ...item,
    active: item.to
      ? route.path === item.to ||
      (item.to !== '/' && route.path.startsWith(item.to))
      : false
  }))
})
</script>

<style scoped>
@reference "../assets/css/main.css";

.mega-menu {
  @apply w-full pt-0 min-h-150  shadow-2xl pointer-events-none;
  transition: all 0.3s var(--ease-snappy);
  transform: translateY(-100%);
  opacity: 0;
}

.mega-menu.active {
  pointer-events: all;
  opacity: 1;
  transform: translateY(0%);
}

/* Extra: användaren dropdown */
.user-dropdown {
  min-width: 120px;
}
</style>
